import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { subscribeSharedFirestore } from './sharedFirestoreSubscription';
import { financialYearForDate, paymentStatusOf } from '../utils/billingUtils';

const companiesCollection = collection(firestore, 'billingCompanies');
const proformasCollection = collection(firestore, 'proformas');

export function subscribeBillingCompanies(onData, onError) {
  const handleRows = (rawRows) => {
    const records = [...rawRows];
    records.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    onData(records);
  };

  return subscribeSharedFirestore({
    key: 'billingCompanies:all',
    onData: handleRows,
    onError,
    start: (emit, emitError) => onSnapshot(
      companiesCollection,
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export function subscribeProformas(onData, onError) {
  const handleRows = (rawRows) => {
    const records = [...rawRows];
    records.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    onData(records);
  };

  return subscribeSharedFirestore({
    key: 'proformas:all',
    onData: handleRows,
    onError,
    start: (emit, emitError) => onSnapshot(
      query(proformasCollection),
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export async function createBillingCompany(values, user) {
  return addDoc(companiesCollection, companyPayload(values, user));
}

export async function updateBillingCompany(id, values, user) {
  return updateDoc(doc(firestore, 'billingCompanies', id), {
    ...companyPayload(values, user),
    updatedAt: serverTimestamp(),
  });
}

function companyPayload(values, user) {
  return {
    name: values.name.trim(),
    legalName: values.legalName?.trim() || values.name.trim(),
    pan: values.pan?.trim().toUpperCase() || '',
    address: values.address?.trim() || '',
    email: values.email?.trim() || '',
    phone: values.phone?.trim() || '',
    gstin: values.gstin?.trim().toUpperCase() || '',
    gstApplicable: Boolean(values.gstApplicable),
    gstRate: Number(values.gstRate || 18),
    prefix: values.prefix?.trim().toUpperCase() || 'PI',
    invoiceTitle: values.invoiceTitle?.trim() || 'Proforma Invoice',
    tagline: values.tagline?.trim() || '',
    headerStyle: values.headerStyle || 'professional',
    logoPath: values.logoPath?.trim() || '',
    footerText: values.footerText?.trim() || 'Computer-generated document',
    signatoryLabel: values.signatoryLabel?.trim() || 'Authorised Signatory',
    paymentTerms: values.paymentTerms?.trim() || '',
    bankDetails: values.bankDetails?.trim() || '',
    isActive: values.isActive !== false,
    updatedById: user?.id || '',
    updatedByName: user?.name || user?.email || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

export async function initializeDefaultBillingCompanies(existing, user) {
  const defaults = [
    { name: 'D.C. Shukla & Co.', legalName: 'D.C. Shukla & Co.', tagline: 'Chartered Accountants', invoiceTitle: 'Proforma Invoice', headerStyle: 'professional', logoPath: '/brand/organisation-logo.png', gstApplicable: true, gstRate: 18, prefix: 'DCS/PI', isActive: true },
    { name: 'Taxempt', legalName: 'Taxempt', tagline: 'Your Global Business Advisors', invoiceTitle: 'Invoice', headerStyle: 'classic', logoPath: '/brand/taxempt-global-logo.jpg', gstApplicable: false, gstRate: 0, prefix: 'TAX/PI', isActive: true },
    { name: 'Taxempt Global Advisors Pvt. Ltd.', legalName: 'Taxempt Global Advisors Private Limited', tagline: 'Your Global Business Advisors', invoiceTitle: 'Invoice', headerStyle: 'classic', logoPath: '/brand/taxempt-global-logo.jpg', gstApplicable: false, gstRate: 0, prefix: 'TGA/PI', isActive: true },
  ];
  const names = new Set(existing.map((item) => String(item.name || '').trim().toLowerCase()));
  const missing = defaults.filter((item) => !names.has(item.name.toLowerCase()));
  await Promise.all(missing.map((item) => createBillingCompany(item, user)));
  return missing.length;
}

async function nextProformaNumber(company, invoiceDate) {
  const fy = financialYearForDate(invoiceDate);
  const key = `${company.id}_${fy}`.replace(/[^A-Za-z0-9_-]/g, '_');
  const counterRef = doc(firestore, 'billingCounters', key);
  return runTransaction(firestore, async (transaction) => {
    const snapshot = await transaction.get(counterRef);
    const next = (snapshot.exists() ? Number(snapshot.data().lastNumber || 0) : 0) + 1;
    transaction.set(counterRef, {
      companyId: company.id,
      financialYear: fy,
      lastNumber: next,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return `${company.prefix || 'PI'}/${fy}/${String(next).padStart(4, '0')}`;
  });
}

function companySnapshot(company) {
  return {
    legalName: company.legalName || company.name,
    pan: company.pan || '',
    address: company.address || '',
    email: company.email || '',
    phone: company.phone || '',
    gstin: company.gstin || '',
    gstApplicable: Boolean(company.gstApplicable),
    gstRate: Number(company.gstRate || 0),
    bankDetails: company.bankDetails || '',
    paymentTerms: company.paymentTerms || '',
    name: company.name || company.legalName || '',
    invoiceTitle: company.invoiceTitle || 'Proforma Invoice',
    tagline: company.tagline || '',
    headerStyle: company.headerStyle || 'professional',
    logoPath: company.logoPath || '',
    footerText: company.footerText || 'Computer-generated document',
    signatoryLabel: company.signatoryLabel || 'Authorised Signatory',
  };
}

function dateOrNull(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export async function createProforma(values, context) {
  const { companies, clients, assignments, quickWorks = [], user } = context;
  const company = companies.find((item) => item.id === values.companyId);
  const client = clients.find((item) => item.id === values.clientId);
  if (!company || !client) throw new Error('Billing company and client are required.');

  const selectedAssignments = assignments.filter((item) => (values.assignmentIds || []).includes(item.id));
  const selectedQuickWorks = quickWorks.filter((item) => (values.quickWorkIds || []).includes(item.id));
  const alreadyLinked = selectedAssignments.find((item) => item.proformaId && item.billingStatus !== 'Cancelled');
  if (alreadyLinked) throw new Error(`Assignment "${alreadyLinked.assignmentType || alreadyLinked.category || alreadyLinked.id}" is already linked to an active proforma.`);
  const linkedQuickWork = selectedQuickWorks.find((item) => item.proformaId && item.billingStatus !== 'Cancelled');
  if (linkedQuickWork) throw new Error(`Quick Work "${linkedQuickWork.nature || linkedQuickWork.id}" is already linked to an active proforma.`);

  const automaticNumber = await nextProformaNumber(company, values.proformaDate);
  const number = values.manualNumber?.trim() || automaticNumber;
  const record = {
    proformaNumber: number,
    automaticNumber,
    manualNumberUsed: Boolean(values.manualNumber?.trim()),
    proformaDate: dateOrNull(values.proformaDate),
    dueDate: dateOrNull(values.dueDate),
    companyId: company.id,
    companyName: company.name,
    companySnapshot: companySnapshot(company),
    clientId: client.id,
    clientName: client.name,
    clientSnapshot: {
      pan: client.pan || '', gstin: client.gstin || '', address1: client.address1 || '', address2: client.address2 || '',
      city: client.city || '', state: client.state || '', pinCode: client.pinCode || '', email: client.email || '', mobile: client.mobile || '',
      contactPerson: client.contactPerson || client.contact || '',
      additionalContacts: Array.isArray(client.additionalContacts) ? client.additionalContacts.map((item) => ({ name: item.name || '', mobile: item.mobile || '', isPrimary: Boolean(item.isPrimary) })) : [],
    },
    assignmentIds: selectedAssignments.map((item) => item.id),
    assignmentSnapshots: selectedAssignments.map((item) => ({
      id: item.id, assignmentType: item.assignmentType || item.category || '', financialYear: item.financialYear || '',
      period: item.period || '', matterId: item.matterId || '', matterCode: item.matterCode || '',
    })),
    quickWorkIds: selectedQuickWorks.map((item) => item.id),
    quickWorkSnapshots: selectedQuickWorks.map((item) => ({
      id: item.id, nature: item.nature || '', remarks: item.remarks || '',
    })),
    lineItems: values.lineItems.map((line, index) => ({
      id: line.id || `line-${index + 1}`, assignmentId: line.assignmentId || '', quickWorkId: line.quickWorkId || '', description: line.description.trim(), amount: Number(line.amount || 0),
    })),
    taxableAmount: Number(values.taxableAmount || 0),
    gstRate: Number(values.gstRate || 0),
    gstAmount: Number(values.gstAmount || 0),
    grandTotal: Number(values.grandTotal || 0),
    notes: values.notes?.trim() || '',
    paymentTerms: values.paymentTerms?.trim() || company.paymentTerms || '',
    status: 'Draft',
    sentAt: null,
    cancelledAt: null,
    cancellationReason: '',
    payments: [],
    followUps: [],
    amountChangeHistory: [],
    paymentStatus: 'Unpaid',
    totalPaid: 0,
    outstanding: Number(values.grandTotal || 0),
    nextFollowUpDate: '',
    lastFollowUpDate: '',
    lastFollowUpRemarks: '',
    lastFollowUpOutcome: '',
    createdById: user?.id || '',
    createdByName: user?.name || user?.email || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const proformaRef = await addDoc(proformasCollection, record);
  if (selectedAssignments.length || selectedQuickWorks.length) {
    const batch = writeBatch(firestore);
    selectedAssignments.forEach((assignment) => batch.update(doc(firestore, 'tasks', assignment.id), {
      billingStatus: 'Draft', billingCompleted: false, proformaId: proformaRef.id, proformaNumber: number, updatedAt: serverTimestamp(),
    }));
    selectedQuickWorks.forEach((item) => batch.update(doc(firestore, 'quickWorks', item.id), {
      billingStatus: 'Draft', billingCompleted: false, proformaId: proformaRef.id, proformaNumber: number, updatedAt: serverTimestamp(),
    }));
    await batch.commit();
  }
  return proformaRef;
}

export async function updateProforma(proforma, values, context) {
  if (proforma.status !== 'Draft') throw new Error('Only a Draft proforma can be edited.');
  if ((proforma.payments || []).length) throw new Error('A proforma with recorded payments cannot be edited.');
  const { companies, assignments = [], quickWorks = [], user } = context;
  const company = companies.find((item) => item.id === values.companyId);
  if (!company) throw new Error('Select a valid billing company.');
  const nextAssignmentIds = values.assignmentIds || [];
  const nextQuickWorkIds = values.quickWorkIds || [];
  const selectedAssignments = assignments.filter((item) => nextAssignmentIds.includes(item.id));
  const selectedQuickWorks = quickWorks.filter((item) => nextQuickWorkIds.includes(item.id));
  const duplicateAssignment = selectedAssignments.find((item) => item.proformaId && item.proformaId !== proforma.id && item.billingStatus !== 'Cancelled');
  if (duplicateAssignment) throw new Error(`Assignment "${duplicateAssignment.assignmentType || duplicateAssignment.id}" is already billed elsewhere.`);
  const duplicateQuickWork = selectedQuickWorks.find((item) => item.proformaId && item.proformaId !== proforma.id && item.billingStatus !== 'Cancelled');
  if (duplicateQuickWork) throw new Error(`Quick Work "${duplicateQuickWork.nature || duplicateQuickWork.id}" is already billed elsewhere.`);

  const companyChanged = company.id !== proforma.companyId;
  const newNumber = companyChanged ? await nextProformaNumber(company, values.proformaDate) : proforma.proformaNumber;
  const oldTotal = Number(proforma.grandTotal || 0);
  const newTotal = Number(values.grandTotal || 0);
  const amountChangeHistory = [...(proforma.amountChangeHistory || [])];
  if (Math.abs(oldTotal - newTotal) > 0.009) {
    amountChangeHistory.push({
      id: `${Date.now()}`,
      oldAmount: oldTotal,
      newAmount: newTotal,
      reason: values.changeReason?.trim() || '',
      changedById: user?.id || '',
      changedByName: user?.name || user?.email || '',
      changedAt: new Date().toISOString(),
    });
  }

  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'proformas', proforma.id), {
    companyId: company.id,
    companyName: company.name,
    companySnapshot: companySnapshot(company),
    proformaNumber: newNumber,
    automaticNumber: companyChanged ? newNumber : proforma.automaticNumber,
    manualNumberUsed: companyChanged ? false : Boolean(proforma.manualNumberUsed),
    proformaDate: dateOrNull(values.proformaDate),
    assignmentIds: nextAssignmentIds,
    assignmentSnapshots: selectedAssignments.map((item) => ({ id: item.id, assignmentType: item.assignmentType || item.category || '', financialYear: item.financialYear || '', period: item.period || '', matterId: item.matterId || '', matterCode: item.matterCode || '' })),
    quickWorkIds: nextQuickWorkIds,
    quickWorkSnapshots: selectedQuickWorks.map((item) => ({ id: item.id, nature: item.nature || '', remarks: item.remarks || '' })),
    lineItems: values.lineItems.map((line, index) => ({
      id: line.id || `line-${index + 1}`, assignmentId: line.assignmentId || '', quickWorkId: line.quickWorkId || '', description: line.description.trim(), amount: Number(line.amount || 0),
    })),
    taxableAmount: Number(values.taxableAmount || 0),
    gstRate: Number(values.gstRate || 0),
    gstAmount: Number(values.gstAmount || 0),
    grandTotal: newTotal,
    outstanding: Math.max(0, newTotal - (proforma.payments || []).reduce((sum, item) => sum + Number(item.amount || 0), 0)),
    dueDate: dateOrNull(values.dueDate),
    notes: values.notes?.trim() || '',
    paymentTerms: values.paymentTerms?.trim() || company.paymentTerms || '',
    amountChangeHistory,
    updatedAt: serverTimestamp(),
  });
  const oldAssignmentIds = proforma.assignmentIds || [];
  const oldQuickWorkIds = proforma.quickWorkIds || [];
  oldAssignmentIds.filter((id) => !nextAssignmentIds.includes(id)).forEach((id) => batch.update(doc(firestore, 'tasks', id), {
    billingStatus: 'Not Billed', billingCompleted: false, proformaId: '', proformaNumber: '', updatedAt: serverTimestamp(),
  }));
  nextAssignmentIds.forEach((id) => batch.update(doc(firestore, 'tasks', id), {
    billingStatus: 'Draft', billingCompleted: false, proformaId: proforma.id, proformaNumber: newNumber, updatedAt: serverTimestamp(),
  }));
  oldQuickWorkIds.filter((id) => !nextQuickWorkIds.includes(id)).forEach((id) => batch.update(doc(firestore, 'quickWorks', id), {
    billingStatus: 'Not Billed', billingCompleted: false, proformaId: '', proformaNumber: '', updatedAt: serverTimestamp(),
  }));
  nextQuickWorkIds.forEach((id) => batch.update(doc(firestore, 'quickWorks', id), {
    billingStatus: 'Draft', billingCompleted: false, proformaId: proforma.id, proformaNumber: newNumber, updatedAt: serverTimestamp(),
  }));
  return batch.commit();
}

export async function markProformaSent(proforma, user, sentMode = 'Other') {
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'proformas', proforma.id), {
    status: 'Sent',
    sentAt: serverTimestamp(),
    sentMode: sentMode || 'Other',
    sentById: user?.id || '',
    sentByName: user?.name || user?.email || '',
    updatedAt: serverTimestamp(),
  });
  (proforma.assignmentIds || []).forEach((assignmentId) => batch.update(doc(firestore, 'tasks', assignmentId), {
    billingStatus: 'Sent', billingCompleted: false, updatedAt: serverTimestamp(),
  }));
  (proforma.quickWorkIds || []).forEach((id) => batch.update(doc(firestore, 'quickWorks', id), {
    billingStatus: 'Sent', billingCompleted: false, updatedAt: serverTimestamp(),
  }));
  return batch.commit();
}

export async function cancelProforma(proforma, reason, user) {
  if ((proforma.payments || []).length) throw new Error('A proforma with recorded payments cannot be cancelled.');
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'proformas', proforma.id), {
    status: 'Cancelled', paymentStatus: 'Unpaid', cancellationReason: reason.trim(), cancelledAt: serverTimestamp(),
    cancelledById: user?.id || '', cancelledByName: user?.name || user?.email || '', updatedAt: serverTimestamp(),
  });
  (proforma.assignmentIds || []).forEach((assignmentId) => batch.update(doc(firestore, 'tasks', assignmentId), {
    billingStatus: 'Not Billed', billingCompleted: false, proformaId: '', proformaNumber: '', updatedAt: serverTimestamp(),
  }));
  (proforma.quickWorkIds || []).forEach((id) => batch.update(doc(firestore, 'quickWorks', id), {
    billingStatus: 'Not Billed', billingCompleted: false, proformaId: '', proformaNumber: '', updatedAt: serverTimestamp(),
  }));
  return batch.commit();
}

export async function addFollowUp(proforma, values, user) {
  const entry = {
    id: `${Date.now()}`,
    date: values.date,
    remarks: values.remarks.trim(),
    outcome: values.outcome || 'Reminder sent',
    nextFollowUpDate: values.nextFollowUpDate || '',
    addedById: user?.id || '',
    addedByName: user?.name || user?.email || '',
    addedAt: new Date().toISOString(),
  };
  const followUps = [...(proforma.followUps || []), entry];
  return updateDoc(doc(firestore, 'proformas', proforma.id), {
    followUps,
    lastFollowUpDate: entry.date,
    lastFollowUpRemarks: entry.remarks,
    lastFollowUpOutcome: entry.outcome,
    nextFollowUpDate: entry.nextFollowUpDate,
    updatedAt: serverTimestamp(),
  });
}

export async function addConsolidatedFollowUp(proformas, values, user) {
  const batch = writeBatch(firestore);
  const stamp = Date.now();
  proformas.forEach((proforma, index) => {
    const entry = {
      id: `${stamp}-${index}`,
      date: values.date,
      remarks: values.remarks.trim(),
      outcome: values.outcome || 'Reminder sent',
      nextFollowUpDate: values.nextFollowUpDate || '',
      consolidated: true,
      addedById: user?.id || '',
      addedByName: user?.name || user?.email || '',
      addedAt: new Date().toISOString(),
    };
    batch.update(doc(firestore, 'proformas', proforma.id), {
      followUps: [...(proforma.followUps || []), entry],
      lastFollowUpDate: entry.date,
      lastFollowUpRemarks: entry.remarks,
      lastFollowUpOutcome: entry.outcome,
      nextFollowUpDate: entry.nextFollowUpDate,
      updatedAt: serverTimestamp(),
    });
  });
  return batch.commit();
}

export async function addPayment(proforma, values, user) {
  if (proforma.status === 'Cancelled') throw new Error('Payment cannot be recorded against a cancelled proforma.');
  const payments = [...(proforma.payments || []), {
    id: `${Date.now()}`, date: values.date, amount: Number(values.amount || 0), mode: values.mode || '',
    reference: values.reference?.trim() || '', remarks: values.remarks?.trim() || '', addedById: user?.id || '',
    addedByName: user?.name || user?.email || '', addedAt: new Date().toISOString(),
  }];
  const paymentStatus = paymentStatusOf({ ...proforma, payments });
  const totalPaid = payments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const outstanding = Math.max(0, Number(proforma.grandTotal || 0) - totalPaid);
  const batch = writeBatch(firestore);
  batch.update(doc(firestore, 'proformas', proforma.id), {
    payments,
    paymentStatus,
    totalPaid,
    outstanding,
    paidAt: paymentStatus === 'Paid' ? serverTimestamp() : null,
    nextFollowUpDate: paymentStatus === 'Paid' ? '' : (proforma.nextFollowUpDate || ''),
    updatedAt: serverTimestamp(),
  });
  (proforma.assignmentIds || []).forEach((assignmentId) => batch.update(doc(firestore, 'tasks', assignmentId), {
    billingStatus: paymentStatus === 'Paid' ? 'Paid' : 'Partially Paid',
    billingCompleted: paymentStatus === 'Paid',
    updatedAt: serverTimestamp(),
  }));
  (proforma.quickWorkIds || []).forEach((id) => batch.update(doc(firestore, 'quickWorks', id), {
    billingStatus: paymentStatus === 'Paid' ? 'Paid' : 'Partially Paid',
    billingCompleted: paymentStatus === 'Paid',
    updatedAt: serverTimestamp(),
  }));
  return batch.commit();
}
